import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { bugReportService } from '../services/bugReportService'
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

interface BugReportModalProps {
  isOpen: boolean
  onClose: () => void
}

const BugReportModal = ({ isOpen, onClose }: BugReportModalProps) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    category: 'general' as 'general' | 'ui' | 'performance' | 'data' | 'auth' | 'billing',
    steps: '',
    expectedBehavior: '',
    actualBehavior: '',
    browserInfo: navigator.userAgent,
    url: window.location.href
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setIsSubmitting(true)
    setError(null)

    try {
      await bugReportService.submitBugReport({
        ...formData,
        userId: user.id,
        userEmail: user.email || '',
        userName: `${user.firstName} ${user.lastName}`.trim()
      })
      
      setIsSubmitted(true)
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose()
        setIsSubmitted(false)
        setFormData({
          title: '',
          description: '',
          severity: 'medium',
          category: 'general',
          steps: '',
          expectedBehavior: '',
          actualBehavior: '',
          browserInfo: navigator.userAgent,
          url: window.location.href
        })
      }, 3000)
    } catch (err) {
      console.error('Error submitting bug report:', err)
      setError('Failed to submit bug report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const severityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-400', description: 'Minor issue, cosmetic problem' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-400', description: 'Affects functionality but has workaround' },
    { value: 'high', label: 'High', color: 'text-orange-400', description: 'Major functionality broken' },
    { value: 'critical', label: 'Critical', color: 'text-red-400', description: 'App unusable, data loss risk' }
  ]

  const categoryOptions = [
    { value: 'general', label: 'General', description: 'General issues or feedback' },
    { value: 'ui', label: 'User Interface', description: 'Layout, design, or visual issues' },
    { value: 'performance', label: 'Performance', description: 'Slow loading, lag, or crashes' },
    { value: 'data', label: 'Data Issues', description: 'Data not saving, loading, or syncing' },
    { value: 'auth', label: 'Authentication', description: 'Login, signup, or permission issues' },
    { value: 'billing', label: 'Billing', description: 'Payment, subscription, or billing issues' }
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-2xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
          >
            {isSubmitted ? (
              // Success State
              <div className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center"
                >
                  <CheckCircleIcon className="w-8 h-8 text-green-400" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-2">Bug Report Submitted!</h2>
                <p className="text-gray-400 mb-4">
                  Thank you for helping us improve ShootSuite. We'll review your report and get back to you if needed.
                </p>
                <p className="text-sm text-gray-500">
                  This window will close automatically...
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                      <ExclamationTriangleIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Report a Bug</h2>
                      <p className="text-gray-400 text-sm">Help us improve ShootSuite</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Form */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Bug Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Brief description of the issue"
                        required
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all"
                      />
                    </div>

                    {/* Category and Severity */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all"
                        >
                          {categoryOptions.map(option => (
                            <option key={option.value} value={option.value} className="bg-gray-800">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Severity
                        </label>
                        <select
                          value={formData.severity}
                          onChange={(e) => handleInputChange('severity', e.target.value)}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all"
                        >
                          {severityOptions.map(option => (
                            <option key={option.value} value={option.value} className="bg-gray-800">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Description *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Describe the bug in detail..."
                        required
                        rows={4}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all resize-none"
                      />
                    </div>

                    {/* Steps to Reproduce */}
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Steps to Reproduce
                      </label>
                      <textarea
                        value={formData.steps}
                        onChange={(e) => handleInputChange('steps', e.target.value)}
                        placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                        rows={3}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all resize-none"
                      />
                    </div>

                    {/* Expected vs Actual Behavior */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Expected Behavior
                        </label>
                        <textarea
                          value={formData.expectedBehavior}
                          onChange={(e) => handleInputChange('expectedBehavior', e.target.value)}
                          placeholder="What should happen?"
                          rows={3}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all resize-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Actual Behavior
                        </label>
                        <textarea
                          value={formData.actualBehavior}
                          onChange={(e) => handleInputChange('actualBehavior', e.target.value)}
                          placeholder="What actually happens?"
                          rows={3}
                          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-all resize-none"
                        />
                      </div>
                    </div>

                    {/* Technical Info */}
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <h3 className="text-sm font-medium text-gray-400 mb-2">Technical Information</h3>
                      <div className="space-y-2 text-xs text-gray-500">
                        <div><strong>URL:</strong> {formData.url}</div>
                        <div><strong>Browser:</strong> {formData.browserInfo}</div>
                        <div><strong>User:</strong> {user?.email}</div>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <motion.button
                        type="submit"
                        disabled={isSubmitting || !formData.title || !formData.description}
                        className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
                      </motion.button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default BugReportModal 